import { SlideModelSdkTypeV3 } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import {
    CopiedKeyType,
    CopyElementType,
    IClipboardData,
    ICopiedLayoutData,
    ICopiedMasterData,
    ICopiedNoteSlideData,
    ICopiedSdkData,
    ICopiedSlideData,
    ICopiedThemeData,
    ISlideClipboardData,
    ISlideSize,
} from '@src/slide/domain/ui-model/element-modeles/property/copy/copy-model/paste-special.interface';
import { IDrawingShapeData, IPresentationDataModel, ISlideData, ILayoutData, IShapeData } from '@tencent/slide-sdk-model';
import { transaction as sdkTransaction } from '@src/slide/domain/slide-app';
import cloneDeep from 'lodash/cloneDeep';
import { DeduplicateDirector } from '@tencent/slide-sdk-model/src/utils/deduplicate/deduplicate-director';
import { DeduplicateBuilder } from '@tencent/slide-sdk-model/src/utils/deduplicate/deduplicate-builder';
import { IDeduplicatePresentationData } from '@tencent/slide-sdk-model/src/utils/deduplicate/interface/deduplication.interface';
import BasePasteSpecial from './paste-base-strategy';
import { IPasteSpecialServiceDelegates, IPasteSpecialStrategy } from './interface';
import { ElementSdkDataModify } from '@src/slide/application/service/copy-paste/core/paste/element-sdk-data-modify';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { PresentationMutationAPI } from '@tencent/slide-sdk-model/src/api/presentation-mutation-api';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';

// 保留源格式粘贴 inheritedData
export default class PasteSourceFormat extends BasePasteSpecial implements IPasteSpecialStrategy {
    public constructor(copyInfo, services: IPasteSpecialServiceDelegates) {
        super(copyInfo, services);
    }

    public paste() {
        this.textSelectivePasteState?.beforePaste();
        // 先执行undo操作，撤销使用目标主题粘贴
        this.undoRedoService?.undo();

        // 执行粘贴操作
        const pasteHandlerMap = {
            [CopyElementType.SHAPE]: this.pasteShape,
            [CopyElementType.SLIDE]: this.pasteSlide,
            [CopyElementType.TEXT]: this.pasteText,
        };
        pasteHandlerMap[this.type]?.call(this);

        // 数据上报
        this.report();
    }

    private pasteText() {
        // 需要等待撤回之后光标更新到正确位置再执行新的粘贴策略
        const off = this.textSelectionUndoRedo?.afterUndoRedo(() => {
            // eslint-disable-next-line max-len
            this.copyPasteManager?.dangerousInstance?.runTextSelectivePaste((this.clipboardData.data[0] as ICopiedSdkData<IShapeData>).inheritedSdkData);
            off?.();
            this.textSelectivePasteState?.afterPaste();
        });
    }

    /**
     * 粘贴形状
     */
    private pasteShape(): void {
        const cloneClipboardData = cloneDeep(this.clipboardData);
        const cloneDataList = cloneClipboardData.data;
        const inheritedSdkDataList = cloneDataList.map((allSdkData) => {
            const { inheritedSdkData } = allSdkData;
            ElementSdkDataModify.adjustElementPosition(
                inheritedSdkData as IDrawingShapeData,
                this.selectionService?.currentSlide,
                CopiedKeyType.INHERIT,
            );
            return inheritedSdkData;
        }) as IDrawingShapeData[];
        const api = (this.selectionService?.currentSlide?.sdkData as SlideModelSdkTypeV3)?.api;
        api?.addShapes(inheritedSdkDataList);
    }

    /**
     * 粘贴幻灯片
     */
    private pasteSlide(): void {
        const cloneClipboardData = cloneDeep(this.clipboardData);
        const copiedDocId = cloneClipboardData.globalPadId;
        const currentDocId = SlideDataServices.DocInfoManager()?.globalPadId;
        const sdkData = this.selectionService?.uiDataService.presentation.sdkData as IPresentationDataModel;
        // 同一文档粘贴
        if (copiedDocId === currentDocId) {
            this.pasteSlideInSameDoc(cloneClipboardData, sdkData);
            return;
        }

        // 跨文档粘贴
        this.pasteSlideInDiffDoc(cloneClipboardData, sdkData);
    }

    /**
     * 同一文档中粘贴幻灯片
     * @param clipboardData 剪贴板数据
     * @param sdkData 当前文档sdk数据
     */
    private pasteSlideInSameDoc(clipboardData: IClipboardData, sdkData: IPresentationDataModel): void {
        // 在同一文档中，使用源格式粘贴幻灯片，直接使用未处理过的原始数据
        const ownSdkDataList = clipboardData.data.map(allSdkData => allSdkData[CopiedKeyType.OWN]) as ISlideData[];
        if (sdkData.sectionList?.length) {
            sdkData.api.addSlidesWithSection(ownSdkDataList, this.insertGapIndex);
        } else {
            sdkData.api.addSlides(ownSdkDataList, this.insertGapIndex);
        }
    }

    /**
     * 跨文档粘贴幻灯片
     * @param clipboardData 剪贴板数据
     * @param sdkData 当前文档sdk数据
     */
    private pasteSlideInDiffDoc(clipboardData: IClipboardData, sdkData: IPresentationDataModel): void {
        // 跨文档，使用源格式粘贴幻灯片，不能直接使用继承数据
        // 需要根据复制的数据，添加主题、添加母版、添加版式、 添加slide、 添加注释
        const { api } = sdkData;
        sdkTransaction(() => {
            // 幻灯片保留原格式粘贴时，需要对母版、版式进行去重
            const deduplicateData = this.getDeduplicateData(clipboardData as ISlideClipboardData, sdkData);

            const {
                data = [] as ICopiedSlideData[],
                themeList = [] as ICopiedThemeData[],
                masterList = [] as ICopiedMasterData[],
                layoutList = [] as ICopiedLayoutData[],
                noteList = [] as ICopiedNoteSlideData[],
                slideSize,
            } = deduplicateData;

            // 1、添加主题
            const themeIdsMap = this.addTheme(themeList, api);

            // 2、添加母版，并建立新母版和新主题的关系
            const masterIdsMapArr = this.addMasters(masterList, api, themeIdsMap);

            // 3、添加版式，并建立新母版和新版式的关系
            const layoutIdsMapArr = this.addLayouts(layoutList, api, masterIdsMapArr);

            // 4、添加Slide，并建立slide和layoutId的关系
            const slideIdsMapArr = this.addSlides({
                data,
                api,
                layoutIdsMapArr,
                hasSection: !!sdkData.sectionList?.length,
                slideSize,
            });

            // 5、添加备注, 并建立新slide 和新note的关联
            this.addNotes(noteList, api, slideIdsMapArr);
        });
    }

    /**
     * 获取去重后的剪贴板数据
     * @param clipboardData 剪贴板数据
     * @param sdkData 当前文档sdk数据
     * @returns 去重后的剪贴板数据
     */
    private getDeduplicateData(
        clipboardData: ISlideClipboardData,
        sdkData: IPresentationDataModel,
    ): ISlideClipboardData {
        const { ownedData, inheritedData } = this.clipboardDataToSdkData(clipboardData);
        const deduplicateBuilder = new DeduplicateBuilder();
        const deduplicateDirector = new DeduplicateDirector(deduplicateBuilder);

        const newOwnedData = deduplicateDirector.directDeduplicate(ownedData, sdkData.getOwnedData());
        const newInheritedData = deduplicateDirector.directDeduplicate(inheritedData, sdkData.getOwnedData(), {
            disableOrganizeData: true,
        });
        return {
            ...clipboardData,
            ...this.sdkDataToClipboardData({ ownedData: newOwnedData, inheritedData: newInheritedData }),
        };
    }

    private clipboardDataToSdkData(clipboardData: ISlideClipboardData): {
        ownedData: IDeduplicatePresentationData;
        inheritedData: IDeduplicatePresentationData;
    } {
        const getPresentationData = (type: CopiedKeyType) => ({
            slideList: clipboardData.data?.map(i => i[type]) ?? [],
            themeList: clipboardData.themeList?.map(i => i[type]) ?? [],
            masterList: clipboardData.masterList?.map(i => i[type]) ?? [],
            layoutMap: clipboardData.layoutList.reduce<Record<string, ILayoutData>>((newLayoutMap, layout) => {
                newLayoutMap[layout[type].id] = layout[type];
                return newLayoutMap;
            }, {}),
        });

        return {
            ownedData: getPresentationData(CopiedKeyType.OWN),
            inheritedData: getPresentationData(CopiedKeyType.INHERIT),
        };
    }

    private sdkDataToClipboardData(sdkData: {
        ownedData: IDeduplicatePresentationData;
        inheritedData: IDeduplicatePresentationData;
    }): Partial<ISlideClipboardData> {
        const ownedSlideList = sdkData.ownedData.slideList ?? [];
        const inheritedSlideList = sdkData.inheritedData.slideList ?? [];
        const ownedThemeList = sdkData.ownedData.themeList ?? [];
        const inheritedThemeList = sdkData.inheritedData.themeList ?? [];
        const ownedMasterList = sdkData.ownedData.masterList ?? [];
        const inheritedMasterList = sdkData.inheritedData.masterList ?? [];
        const ownedLayoutList = Object.values(sdkData.ownedData.layoutMap ?? {});
        const inheritedLayoutList = Object.values(sdkData.inheritedData.layoutMap ?? {});
        return {
            data: ownedSlideList.map((_, index) => ({
                [CopiedKeyType.OWN]: ownedSlideList[index],
                [CopiedKeyType.INHERIT]: inheritedSlideList[index],
            })),
            themeList: ownedThemeList.map((_, index) => ({
                [CopiedKeyType.OWN]: ownedThemeList[index],
                [CopiedKeyType.INHERIT]: inheritedThemeList[index],
            })),
            masterList: ownedMasterList.map((_, index) => ({
                [CopiedKeyType.OWN]: ownedMasterList[index],
                [CopiedKeyType.INHERIT]: inheritedMasterList[index],
            })),
            layoutList: ownedLayoutList.map((_, index) => ({
                [CopiedKeyType.OWN]: ownedLayoutList[index],
                [CopiedKeyType.INHERIT]: inheritedLayoutList[index],
            })),
        };
    }

    /**
     * 添加主题
     * @param themeList 主题sdk数据
     * @param api PresentationMutationAPI
     * @returns Map 旧themeID => 新themeID
     */
    private addTheme(themeList: ICopiedThemeData[], api: PresentationMutationAPI): Map<string, string> {
        let themeIdsMap = new Map();
        if (themeList.length) {
            const themeOwnList = themeList.map((copiedThemeData: ICopiedThemeData) => copiedThemeData.ownSdkData);
            // 旧ID => 新ID
            themeIdsMap = api.addTheme(themeOwnList);
        }

        return themeIdsMap;
    }

    /**
     * 添加母版，并建立新母版和新主题的关系
     * @param masterList 母版sdk数据
     * @param api PresentationMutationAPI
     * @param themeIdsMap 主题映射
     * @returns 母版映射List [Map<旧masterId，新MasterId>]
     */
    private addMasters(
        masterList: ICopiedMasterData[],
        api: PresentationMutationAPI,
        themeIdsMap: Map<string, string>,
    ): Map<string, string>[] {
        let masterIdsMapArr: Map<string, string>[] = [];
        if (masterList.length) {
            const masterOwnList = masterList.map(({ ownSdkData }) => {
                const newOwnSdkData = { ...ownSdkData };
                // 将母版的主题id修改为新生成的主题id，则新母版和新主题则会建立关联
                const newThemeId = themeIdsMap.get(ownSdkData.themeId);
                newThemeId && (newOwnSdkData.themeId = newThemeId);
                return newOwnSdkData;
            });
            masterIdsMapArr = api.addMasters(masterOwnList);
        }

        return masterIdsMapArr;
    }

    /**
     * 添加版式,并建立新母版和新版式的关系
     * @param layoutList 版式SDK数据
     * @param api PresentationMutationAPI
     * @param masterIdsMapArr 母版映射list
     * @returns 版式映射list [Map<旧layoutId，新layoutId>]
     */
    private addLayouts(
        layoutList: ICopiedLayoutData[],
        api: PresentationMutationAPI,
        masterIdsMapArr: Map<string, string>[],
    ): Map<string, string>[] {
        const layoutIdsMapArr: Map<string, string>[] = [];
        if (layoutList.length) {
            layoutList.forEach(({ ownSdkData }) => {
                const newOwnSdkData = { ...ownSdkData };
                const { masterId: oldMasterId } = ownSdkData;

                let newMasterId;
                // eslint-disable-next-line no-restricted-syntax
                for (const masterIdsMap of masterIdsMapArr) {
                    newMasterId = masterIdsMap.get(oldMasterId);
                    if (newMasterId) break;
                }

                newMasterId && (newOwnSdkData.masterId = newMasterId);
                // addLayouts接口仅支持添加同一母版下的版式数据，多母版添加时需要根据母版分开添加
                // 同一事务中逐个添加和分批添加性能无差别，为减少一次分组遍历，此处选择逐个添加
                const idsMapArr = api.addLayouts([newOwnSdkData]);
                layoutIdsMapArr.push(...idsMapArr);
            });
        }

        return layoutIdsMapArr;
    }

    /**
     * 添加Slide，并建立slide和新layoutId的关系
     * @param data 幻灯片SDk数据
     * @param api PresentationMutationAPI
     * @param layoutIdsMapArr 版式映射List
     * @param hasSection 当前文档是否存在节
     * @returns 幻灯片映射list [Map<旧SlideId，新SlideId>]
     */
    private addSlides({
        data,
        api,
        layoutIdsMapArr,
        hasSection,
        slideSize,
    }: {
        data: ICopiedSlideData[];
        api: PresentationMutationAPI;
        layoutIdsMapArr: Map<string, string>[];
        hasSection: boolean;
        slideSize: ISlideSize;
    }): Map<string, string>[] {
        const slideOwnList = data.map(({ ownSdkData }) => {
            const newOwnSdkData = ownSdkData as ISlideData;
            const { layoutId: oldLayoutId } = newOwnSdkData;

            // 根据旧版式id，找到新版式id，并建立slide和layoutId的关系
            let newLayoutId;
            // eslint-disable-next-line no-restricted-syntax
            for (const layoutIdsMap of layoutIdsMapArr) {
                newLayoutId = layoutIdsMap.get(oldLayoutId);
                if (newLayoutId) break;
            }

            // data中slide的重复版式id已经被修改为正确的id，不会出现在layoutIdsArr中
            if (newLayoutId) {
                newOwnSdkData.layoutId = newLayoutId;
            }

            this.slideSdkDataModify.adjustPageData(newOwnSdkData, slideSize, true);

            return newOwnSdkData;
        });

        let slideIdsMapArr: Map<string, string>[] = [];
        if (hasSection) {
            slideIdsMapArr = api.addSlidesWithSection(slideOwnList, this.insertGapIndex);
        } else {
            slideIdsMapArr = api.addSlides(slideOwnList, this.insertGapIndex);
        }

        return slideIdsMapArr;
    }

    /**
     * 添加备注，并建立新slide 和新note的关联
     * @param noteList 幻灯片对应的备注数据
     * @param api PresentationMutationAPI
     * @param slideIdsMapArr 幻灯片id映射list
     */
    private addNotes(
        noteList: ICopiedNoteSlideData[],
        api: PresentationMutationAPI,
        slideIdsMapArr: Map<string, string>[],
    ) {
        noteList.forEach(({ ownSdkData }) => {
            const newOwnSdkData = { ...ownSdkData };
            const { slideId: oldSlideId } = ownSdkData;

            let newSlideId;
            // eslint-disable-next-line no-restricted-syntax
            for (const slideIdsMap of slideIdsMapArr) {
                newSlideId = slideIdsMap.get(oldSlideId);
                if (newSlideId) break;
            }

            newSlideId && (newOwnSdkData.slideId = newSlideId);
            api.addNoteSlide(newOwnSdkData);
        });
    }

    /**
     * 上报粘贴保留源格式
     */
    private report(): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PASTE_SPECIAL,
            action: TDWActionType.SOURCE_FORMAT,
        });
    }
}
